import React, { Component } from "react";
import config from "@config";
import utilityFunctions from "@store/utilityFunctions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Select from "react-select";
import {
  MdFileDownload,
  MdDateRange,
  MdPublic,
  MdErrorOutline,
  MdReceipt,
  MdMail,
  MdNotifications,
} from "react-icons/md";
import "./Reports.css";

const countryOptions = [
  { value: "", label: "All" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

export default class Reports extends Component {
  constructor(props) {
    super(props);

    const userInfo =
      localStorage.getItem("userInfo") || utilityFunctions.getCookie("userInfo");
    this.user = userInfo && JSON.parse(userInfo).user;
    this.userRole = this.user?.role;

    this.state = {
      loading: false,
      fromDate: null,
      toDate: null,
      country: null,
      brandIds: [],
      dateError: "",
      creditClaimOnly: false,
      showProcessingMessage: false,
      submitError: "",
    };
  }

  handleSubmit = (e) => {
    e.preventDefault();
    if (
      this.state.fromDate &&
      this.state.toDate &&
      this.state.fromDate > this.state.toDate
    ) {
      this.setState({
        dateError: '"From" date cannot be later than "To" date.',
      });
      return;
    }
    if (!this.state.brandIds?.length) {
      this.setState({
        dateError: "No accessible brands found for your account.",
      });
      return;
    }
    this.setState({ loading: true, dateError: "", submitError: "" });

    const queryParams = [];
    if (this.state.fromDate) {
      queryParams.push(
        `from_date=${moment(this.state.fromDate).format("YYYY-MM-DD")}`,
      );
    }
    if (this.state.toDate) {
      queryParams.push(
        `to_date=${moment(this.state.toDate).format("YYYY-MM-DD")}`,
      );
    }

    queryParams.push(`brand=${this.state.brandIds.join(",")}`);

    const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";

    if (this.state.creditClaimOnly) {
      this.handleCreditClaimExport(queryString);
      return;
    }

    const url = `${config.backendServer}report/export_report${queryString}`;

    this.props
      .protectedFetch(url, "GET", null, (res) => {
        return res; // Do everything in the 'then'
      })
      .then((res) => {
        if (res.ok) {
          res.blob().then((res) => {
            const blob = res;
            blob.name = "Export_excel_" + new Date().toISOString();
            utilityFunctions.downloadFileAutomatically(blob, res.type);
            this.props.handleCloseModal?.();
          });
        }
      })
      .catch(() => {
        console.log("fail");
        this.setState({ loading: false });
        this.props.handleCloseModal?.();
      });
  };

  // TODO: confirm the real endpoint on integration — this is a placeholder
  // for the async, email-delivered "Credit Claim Only" export.
  handleCreditClaimExport = (queryString) => {
    const url = `${config.backendServer}report/complete_export_commercial_report${queryString}`;

    this.props
      .protectedFetch(url, "GET", null, (res) => res)
      .then((res) => {
        if (res.ok) {
          this.setState({ loading: false, showProcessingMessage: true });
        } else {
          this.setState({
            loading: false,
            submitError: "Something went wrong. Please try again.",
          });
        }
      })
      .catch(() => {
        this.setState({
          loading: false,
          submitError: "Something went wrong. Please try again.",
        });
      });
  };

  componentDidMount() {
    const availableOptions = this.getCountryOptions().filter(
      (o) => !o.isDisabled,
    );
    const countryOnlyOptions = availableOptions.filter((o) => o.value !== "");
    if (countryOnlyOptions.length === 1) {
      const singleCountry = countryOnlyOptions[0].value;
      this.setState({
        country: singleCountry,
        brandIds: (this.user?.brands || [])
          .filter((brand) => brand.country === singleCountry)
          .map((brand) => brand.id),
      });
    }
  }

  getCountrySelectProps(country) {
    const availableOptions = this.getCountryOptions().filter(
      (o) => !o.isDisabled,
    );
    const countryOnlyOptions = availableOptions.filter((o) => o.value !== "");
    const isSingleCountry = countryOnlyOptions.length === 1;
    const displayOptions = isSingleCountry
      ? countryOnlyOptions
      : availableOptions;
    const selectValue = isSingleCountry
      ? countryOnlyOptions[0]
      : country === null
        ? null
        : availableOptions.find((o) => o.value === country) || null;
    return { displayOptions, selectValue, isSingleCountry };
  }

  getCountryOptions() {
    const userBrands = this.user?.brands || [];
    return countryOptions.map((option) => {
      if (option.value === "") return option; // "All" is always enabled
      const hasbrands = userBrands.some(
        (brand) => brand.country === option.value,
      );
      return { ...option, isDisabled: !hasbrands };
    });
  }

  render() {
    const {
      loading,
      fromDate,
      toDate,
      country,
      dateError,
      creditClaimOnly,
      showProcessingMessage,
      submitError,
    } = this.state;

    if (showProcessingMessage) {
      return (
        <div className="card reports-card">
          <div className="card-header">
            <MdNotifications className="header-icon" />
            <h6>Notification</h6>
          </div>
          <div className="card-body reports-processing-body">
            <MdMail size={32} />
            <p>
              The system is processing your request, we will email you once done
            </p>
          </div>
          <div className="card-footer">
            <button
              type="button"
              className="btn btn-primary btn-export"
              onClick={() => this.props.handleCloseModal?.()}
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="card reports-card">
        {/* Header */}
        <div className="card-header">
          <MdFileDownload className="header-icon" />
          <h6>Export Report to Excel</h6>
        </div>

        <div className="card-body">
          {/* Date Range Section */}
          <div className="reports-section-label">
            <MdDateRange size={13} />
            Date Received
          </div>
          <div className="reports-date-group">
            <div className="reports-date-row">
              <div className="reports-date-col">
                <label htmlFor="fromDate">From</label>
                <DatePicker
                  id="fromDate"
                  className="form-control"
                  selected={fromDate}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  maxDate={toDate || undefined}
                  onChange={(val) =>
                    this.setState({ fromDate: val, dateError: "" })
                  }
                />
              </div>
              <div className="reports-date-col">
                <label htmlFor="toDate">To</label>
                <DatePicker
                  id="toDate"
                  className="form-control"
                  selected={toDate}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  minDate={fromDate || undefined}
                  onChange={(val) =>
                    this.setState({ toDate: val, dateError: "" })
                  }
                />
              </div>
            </div>

            {/* Inline date error */}
            {dateError && (
              <div className="reports-error-banner">
                <MdErrorOutline size={15} />
                {dateError}
              </div>
            )}
          </div>

          {/* Country Section */}
          <div className="reports-section-label">
            <MdPublic size={13} />
            Country
          </div>
          <div className="reports-country-group">
            <label htmlFor="report-country">Filter by country</label>
            {(() => {
              const { displayOptions, selectValue } =
                this.getCountrySelectProps(country);
              return (
                <Select
                  inputId="report-country"
                  classNamePrefix="react-select"
                  value={selectValue}
                  placeholder="Select country..."
                  options={displayOptions}
                  onChange={(option) =>
                    this.setState({
                      country: option ? option.value : null,
                      brandIds: option
                        ? (this.user?.brands || [])
                            .filter((brand) =>
                              option.value
                                ? brand.country === option.value
                                : true,
                            )
                            .map((brand) => brand.id)
                        : [],
                    })
                  }
                />
              );
            })()}
          </div>

          {/* Credit Claim Section */}
          <div className="reports-section-label mt-3">
            <MdReceipt size={13} />
            Credit Claim
          </div>
          <div className="reports-checkbox-group">
            <label className="reports-checkbox-label">
              <input
                type="checkbox"
                checked={creditClaimOnly}
                onChange={(e) =>
                  this.setState({ creditClaimOnly: e.target.checked })
                }
              />
              <span>Credit Claim Only</span>
            </label>
          </div>

          {/* Submit error */}
          {submitError && (
            <div className="reports-error-banner">
              <MdErrorOutline size={15} />
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-footer">
          <button
            type="button"
            className="btn btn-outline-secondary btn-cancel"
            onClick={() => this.props.handleCloseModal?.()}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-export"
            onClick={this.handleSubmit}
            disabled={loading || this.state.country === null}
          >
            {loading ? (
              <>
                <span className="reports-spinner" />
                Exporting…
              </>
            ) : (
              <>
                <MdFileDownload size={16} />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
}
